import React, { FC } from 'react';

import {
  getGroupsAssigned,
  getGroupsVisible,
  getMembersAssigned,
  getMembersVisible,
} from '../../../../containers/compose/helpers';
import { ComposeNodeType } from '../../../../containers/compose/props';

import {
  User,
  UserBasicFragment,
  UserGroup,
  UserGroupBasicFragment,
} from '../../../../generated/graphql';
import translate from '../../../../i18n/translate';

import TabSearch from '../TabCheckboxSearch';
import SearchNoResult from '../TabCheckboxSearch/SearchNoResult';

import { getIsEmpty, getIsSearching, getShouldShowNoResults } from './helpers';

export type Props = {
  dataGroupsAll: UserGroupBasicFragment[];
  dataGroupsAssigned: Set<UserGroup['id']>;
  dataMembersAll: UserBasicFragment[];
  dataMembersAssigned: Set<User['id']>;
  dataSearchValue: string;
  isLoading: boolean;
  nodeType?: ComposeNodeType;
  onAssignUpdateGroup: (groupId: UserGroup['id'], state: boolean) => void;
  onAssignUpdateMember: (memberId: User['id'], state: boolean) => void;
  onSearch: (term: string) => void;
};

/**
 * Assignment panel,
 * Consists of a filter box,
 * Members list (with a checkbox for each of them)
 * and group list (where each item is a toggle itself)
 *
 * @param props                      Props passed to the component
 * @param props.dataGroupsAll        All available groups
 * @param props.dataGroupsAssigned   Groups checked
 * @param props.dataMembersAll       All available members
 * @param props.dataMembersAssigned  Members checked
 * @param props.dataSearchValue      <input type="search" /> value attribute
 * @param props.isLoading            Whether the data is being fetched
 * @param props.nodeType             Whether the compose form is being used for messages or templates
 * @param props.onAssignUpdateGroup  Callback to invoke when a group is (un)checked
 * @param props.onAssignUpdateMember Callback to invoke when a member is (un)checked
 * @param props.onSearch             Callback to invoke when search term is changed
 * @returns                          The component itself
 */
const Assignment: FC<Props> = ({
  dataGroupsAll,
  dataGroupsAssigned,
  dataMembersAll,
  dataMembersAssigned,
  dataSearchValue,
  isLoading,
  nodeType = 'MESSAGE',
  onAssignUpdateGroup,
  onAssignUpdateMember,
  onSearch,
}) => {
  const dataGroupsVisible = getGroupsVisible(dataGroupsAll, {
    assigneeGroupsCurrent: dataGroupsAssigned,
    assigneeSearch: dataSearchValue,
  });
  const dataMembersVisible = getMembersVisible(dataMembersAll, {
    assigneeMembersCurrent: dataMembersAssigned,
    assigneeSearch: dataSearchValue,
  });

  const isEmpty = getIsEmpty(dataGroupsVisible, dataMembersVisible);
  const isSearching = getIsSearching(dataSearchValue);

  const shouldShowNoResults = getShouldShowNoResults(
    isEmpty,
    isLoading,
    isSearching,
  );

  if (shouldShowNoResults) {
    return <SearchNoResult nodeType={nodeType} />;
  }

  const assignedGroups = getGroupsAssigned(dataGroupsAll, dataGroupsAssigned);
  const assignedMembers = getMembersAssigned(
    dataMembersAll,
    dataMembersAssigned,
  );

  const commonProps = { isLoading, isSearching };

  return (
    <TabSearch
      dataSearchValue={dataSearchValue}
      onSearch={onSearch}
      placeholder={translate('SEARCH__USER_GROUPS')}
      sections={[
        {
          ...commonProps,
          dataSelected: dataMembersAssigned,
          itemsFiltered: dataMembersVisible,
          itemsSelected: assignedMembers,
          onChange: onAssignUpdateMember,
          type: 'members',
        },
        {
          ...commonProps,
          dataSelected: dataGroupsAssigned,
          itemsFiltered: dataGroupsVisible,
          itemsSelected: assignedGroups,
          onChange: onAssignUpdateGroup,
          type: 'groups',
        },
      ]}
    />
  );
};

export default Assignment;
